import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { type SectionGroup } from '@/components/Autocomplete/sectionUtils';
import { FormAutocompleteField } from '@/components/Form/FormAutocompleteField/FormAutocompleteField';
import { Country, EntityType } from '@/core/graphql/generated/types';

import type { NewEntityCountryIdValuesIn } from '../_schemas/NewEntityCountryId.validation';
import { DcrFormController } from '../DcrFormController/DcrFormController';

import { pickCountryModalStyles as sx } from './PickCountryModal.styles';

const TOP_GDP_COUNTRY_IDS = [
	'us',
	'cn',
	'de',
	'jp',
	'in',
	'gb',
	'fr',
	'ca',
	'it',
	'br',
	'kr',
	'au',
	'ru',
	'mx',
	'es',
	'id',
	'nl',
	'sa',
	'tr',
	'ch'
] as const;

type Props = {
	entityType: EntityType;
	options: Country[];
	preferredCountryId?: string;
};

export const PickCountryModal = ({
	entityType,
	options,
	preferredCountryId
}: Props) => {
	const { t } = useTranslation();
	const {
		control,
		formState: { dirtyFields }
	} = useFormContext<NewEntityCountryIdValuesIn>();

	const suggested = preferredCountryId
		? options.find(c => c.id === preferredCountryId)
		: undefined;

	const topCountries = useMemo(
		() =>
			TOP_GDP_COUNTRY_IDS.map(id => options.find(c => c.id === id)).filter(
				(c): c is Country => c !== undefined
			),
		[options]
	);

	const sectionedOptions: SectionGroup<Country>[] = useMemo(
		() => [
			...(suggested
				? [{ key: 'suggested', label: t('Suggested'), options: [suggested] }]
				: []),
			...(topCountries.length > 0
				? [
						{
							key: 'top',
							label: t('Top countries'),
							options: topCountries
						}
					]
				: []),
			{ key: 'all', label: t('All countries'), options }
		],
		[options, suggested, t, topCountries]
	);

	return (
		<Box sx={sx.root}>
			<Typography variant='h6' component='h2' sx={sx.title}>
				{entityType === EntityType.Hcp
					? t("What is the HCP's country?")
					: t("What is the HCO's country?")}
			</Typography>
			<DcrFormController
				name='newEntityCountryId'
				control={control}
				filterable={false}
				customSx={{
					width: '100%'
				}}
				render={({ field, fieldState }) => (
					<FormAutocompleteField<Country>
						label={''}
						hideLabel
						placeholder={t('Select')}
						id='newEntityCountryId'
						name={field.name}
						section={
							entityType === EntityType.Hcp ? 'professional' : 'organizational'
						}
						sectionedOptions={sectionedOptions}
						defaultValue={field.value ?? ''}
						indicateError={fieldState.error ? true : false}
						isDirty={dirtyFields.newEntityCountryId}
						handleOnDropdownItemSelect={item => {
							field.onChange(item?.id ?? '');
						}}
					/>
				)}
			/>
		</Box>
	);
};
